import { useEffect, useState } from 'react';
import { Link, Navigate, Outlet, useRouterState } from '@tanstack/react-router';

import { useTentarDeNovoSozinho } from '@/lib/falha-de-rede';
import { useSessao } from '@/modulos/sessao/consultas';
import { destinoDoParceiroNaMesa } from '@/modulos/sessao/parceiro';

import { Botao } from './Botao';
import { CoMarcaLogin } from './Marca';
import { Estrutura } from './Estrutura';

/**
 * A ENTRADA, com o MESMO nome na mesa e no app (`/m/entrar`): um endereço só
 * para mandar a qualquer pessoa — `ctfrotas…/entrar` —, e o telefone que o
 * abre cai no app (`lib/celular.ts`). Pedido do dono, 24/10/2026: a mesa usava
 * `/login` e o app `/entrar`.
 */
export const CAMINHO_DO_LOGIN = '/entrar';

/** O nome antigo da entrada da mesa: continua valendo para convites e favoritos já enviados, e redireciona. */
export const CAMINHO_ANTIGO_DO_LOGIN = '/login';

/** A pagina do convite (migracao 0024): como o login, acontece antes de haver sessao, e fora da casca. */
export const CAMINHO_DO_PRIMEIRO_ACESSO = '/primeiro-acesso';

/** A administração da plataforma (0029): acima das empresas, com sessão própria, fora da casca. */
export const CAMINHO_DA_PLATAFORMA = '/plataforma';

/**
 * As páginas para o papel (0035: a folha de campo). Fora da casca para sair
 * sem menu na impressão — mas a API continua exigindo a sessão de empresa.
 */
export const CAMINHO_DA_IMPRESSAO = '/impressao';

/**
 * Decide se a tela recebe a casca da aplicacao — e se ela pode ser desenhada.
 *
 * ## A casca
 *
 * O login e a unica tela sem barra superior e sem navegacao lateral. E a p.24
 * o trata como tela propria, com a co-marca que so ele e o topo do sistema
 * carregam.
 *
 * POR QUE UMA CONDICAO NA RAIZ E NAO UMA ROTA DE LAYOUT
 * O TanStack tem rota de layout sem caminho, que seria a forma canonica de ter
 * dois grupos de telas. Ela custa caro aqui: o id de toda rota filha passa a
 * carregar o id do layout (`/equipamentos` viraria `/casca/equipamentos`), e os
 * `getRouteApi('/equipamentos')` e `getRouteApi('/combustivel/lancamento')` das
 * paginas existentes quebrariam junto. Com uma rota fora da casca, a condicao
 * aqui custa tres linhas e nenhum id muda.
 *
 * ## A PORTA — o que mudou nesta rodada
 *
 * Ate aqui nao havia sessao e nenhuma rota exigia nada: `/equipamentos` abria
 * direto, e a tela caia numa massa de demonstracao quando a API respondia 401.
 * Agora ha entrada de verdade, e a casca pergunta UMA vez quem esta dentro.
 *
 * Sem sessao, nenhuma tela de modulo e desenhada. Nao e zelo de seguranca — a
 * seguranca mora no banco, e cada rota da API ja responde 401 sozinha. E
 * HONESTIDADE DE TELA: desenhar a lista da frota vazia, com estado vazio e
 * tudo, faria parecer que a empresa nao tem equipamento. O que houve foi outra
 * coisa, e a tela diz qual, com o caminho para resolver.
 *
 * ENQUANTO A PERGUNTA ESTA NO AR a casca mostra que esta carregando, e nao a
 * tela de "entre no sistema". Um piscar de "sessao expirada" a cada
 * recarregamento ensina a ignorar a mensagem — e ela e a mesma que aparece
 * quando a sessao expirou de verdade.
 */
export function Casca() {
  const noLogin = useRouterState({
    select: (estado) =>
      estado.location.pathname === CAMINHO_DO_LOGIN ||
      estado.location.pathname === CAMINHO_ANTIGO_DO_LOGIN ||
      estado.location.pathname === CAMINHO_DO_PRIMEIRO_ACESSO ||
      /* A administração da plataforma (0029) tem sessão própria, e não é tela de empresa. */
      estado.location.pathname === CAMINHO_DA_PLATAFORMA ||
      estado.location.pathname.startsWith(`${CAMINHO_DA_PLATAFORMA}/`) ||
      estado.location.pathname.startsWith(`${CAMINHO_DA_IMPRESSAO}/`),
  });
  const caminho = useRouterState({ select: (estado) => estado.location.pathname });
  const sessao = useSessao();
  /* SEM RESPOSTA, TENTA DE NOVO SOZINHA (item 3 dos robos): a API reiniciando
     numa implantacao volta em segundos, e a tela parada no recado ficava
     parada ate alguem clicar. */
  useTentarDeNovoSozinho(sessao.isError, () => {
    void sessao.refetch();
  });

  /* O login desenha a si mesmo em qualquer estado: e para la que todo o resto
     manda a pessoa, e uma porta que espera a resposta de "quem sou eu" para
     aparecer e uma porta que nao abre quando a resposta e "ninguem". */
  if (noLogin) return <Outlet />;

  if (sessao.isPending) return <Esperando />;

  if (sessao.isError) {
    return (
      <Recado
        titulo="O sistema não respondeu"
        descricao="Não foi possível falar com o servidor para saber quem está no sistema. Pode ser a sua conexão ou o sistema reiniciando por uma atualização: a tela tenta de novo sozinha. Se passar de alguns minutos, avise o suporte com o horário."
        acao={
          <Botao
            nivel="primario"
            onClick={() => {
              void sessao.refetch();
            }}
          >
            Tentar de novo
          </Botao>
        }
      />
    );
  }

  if (sessao.data === null) {
    return (
      <Recado
        titulo="Sua sessão não está aberta"
        descricao="Entre com o seu e-mail e a sua senha para ver a frota, os cadastros e os lançamentos da sua empresa. Cada tela mostra o dado da empresa da sua credencial, e de nenhuma outra."
        acao={
          <Link to={CAMINHO_DO_LOGIN} className="botao botao-primario">
            Entrar no sistema
          </Link>
        }
      />
    );
  }

  /* A PESSOA DE POSTO PARCEIRO (decisão 58, 0132) só tem o lançamento: todo
     outro endereço, inclusive o painel inicial e o que vier de um link
     antigo, volta para ele. O servidor recusa o resto de qualquer jeito; a
     volta poupa a pessoa de uma tela que só diz "sem permissão". */
  if (sessao.data.parceiro) {
    const destino = destinoDoParceiroNaMesa(caminho);
    if (destino !== null) return <Navigate to={destino} replace />;
    return <Estrutura parceiro />;
  }

  return <Estrutura />;
}

/**
 * O carregamento da casca — o ESQUELETO DA PROPRIA CASCA, e FALANTE se demorar.
 *
 * A primeira versao era so "Carregando o sistema…", centralizado na tela. O
 * dono pediu esqueleto em todo carregamento (04/10/2026), e o manual manda o
 * mesmo (p.32): o carregando e a forma do conteudo. A objecao antiga — uma tela
 * cheia de cinza para uma pergunta de dezenas de milissegundos pisca mais do
 * que informa — vale para esqueleto que NAO TEM a forma do que vem. Este tem:
 * o menu flutuante, a barra superior e o cabecalho da pagina estao nos mesmos
 * lugares e medidas da casca de verdade, entao a resposta rapida so preenche o
 * que ja estava desenhado, sem nada mudar de lugar.
 *
 * O que nao muda e a resposta que NUNCA vem. Medido em QAS, com a API ainda nao
 * implantada: a tela ficava esperando e nao dizia nada. Quem olhava nao tinha
 * como saber se era lentidao, conexao ou servidor fora do ar — e a regra do
 * projeto e que mensagem diz o que FAZER. Depois de quatro segundos, a frase
 * aparece no lugar do conteudo.
 */
const DEMORA_DEMAIS_MS = 4_000;

/* Larguras dos itens do menu: nome de modulo de verdade varia. */
const ITENS_DO_MENU = [
  ['w-28', 'w-20'],
  ['w-24', 'w-32', 'w-20', 'w-28'],
  ['w-32', 'w-24', 'w-28'],
  ['w-20', 'w-28', 'w-24'],
] as const;

function Esperando() {
  const [demorando, setDemorando] = useState(false);

  useEffect(() => {
    const relogio = setTimeout(() => {
      setDemorando(true);
    }, DEMORA_DEMAIS_MS);
    return () => {
      clearTimeout(relogio);
    };
  }, []);

  return (
    <div role="status" aria-busy="true" className="relative flex h-full bg-tela">
      <span className="sr-only">Carregando o sistema</span>

      {/* O menu flutuante (p.34): 248 px, raio 20, a 12 px das bordas; trilho de
          72 abaixo de 1280 e fora abaixo de 1024 — as medidas da `Navegacao`. */}
      <div
        aria-hidden="true"
        className="my-3 ml-3 flex w-[var(--menu-largura)] shrink-0 flex-col gap-6 overflow-hidden rounded-painel border border-borda-sutil bg-card p-3 shadow-superficie max-[1280px]:w-[var(--menu-trilho)] max-[1280px]:p-2 max-[1024px]:hidden"
      >
        <span className="esqueleto block h-10 w-full rounded-controle" />
        {ITENS_DO_MENU.map((grupo, g) => (
          <div key={g} className="flex flex-col gap-1">
            <span className="esqueleto mx-3 mb-2 block h-2 w-16 max-[1280px]:hidden" />
            {grupo.map((largura, i) => (
              <span key={i} className="flex h-10 items-center gap-3 px-3 max-[1280px]:justify-center max-[1280px]:px-0">
                <span className="esqueleto block size-5 shrink-0 rounded-etiqueta" />
                <span className={`esqueleto block h-3 ${largura} max-[1280px]:hidden`} />
              </span>
            ))}
          </div>
        ))}
        <span className="esqueleto mt-auto block h-16 w-full rounded-controle max-[1280px]:h-10" />
      </div>

      <div aria-hidden="true" className="flex min-h-0 min-w-0 flex-1 flex-col">
        {/* A barra superior sem fundo: a trilha a esquerda; busca, avisos, tema e
            a pessoa a direita. */}
        <div className="flex h-[var(--barra-superior)] shrink-0 items-center gap-4 pr-8 pl-6 max-[1280px]:pr-6 max-[768px]:px-4">
          <span className="esqueleto block h-3 w-40 max-[768px]:hidden" />
          <span className="flex-1" />
          <span className="esqueleto block h-10 w-64 rounded-pill max-[1024px]:hidden" />
          <span className="esqueleto block size-10 rounded-pill" />
          <span className="esqueleto block size-10 rounded-pill max-[768px]:hidden" />
          <span className="flex items-center gap-2">
            <span className="esqueleto block size-8 rounded-pill" />
            <span className="esqueleto block h-3 w-24 max-[768px]:hidden" />
          </span>
        </div>

        <div className="pagina min-h-0 flex-1 overflow-hidden">
          {/* O cabecalho da pagina: sobretitulo, titulo e descricao. */}
          <div className="flex flex-col gap-3">
            <span className="esqueleto block h-2 w-20" />
            <span className="esqueleto block h-8 w-72 max-w-full" />
            <span className="esqueleto block h-3 w-96 max-w-full" />
          </div>
          {demorando ? null : (
            <div className="cartao flex flex-col gap-4 p-4">
              {[0, 1, 2, 3, 4].map((i) => (
                <span key={i} className={`esqueleto block h-3 ${i % 2 === 0 ? 'w-full' : 'w-4/5'}`} />
              ))}
            </div>
          )}
        </div>
      </div>

      {demorando ? (
        /* Fora do `aria-hidden`: e a unica frase da tela, e diz o que fazer.
           `--text-secondary`: sobre o fundo da tela o muted mede 4,15:1. */
        <p className="txt-apoio absolute inset-x-0 top-1/2 mx-auto max-w-[48ch] px-6 text-center text-tinta-secundaria">
          Está demorando mais que o normal. O servidor pode estar fora do ar ou ainda subindo; se continuar, avise quem
          administra o sistema.
        </p>
      ) : null}
    </div>
  );
}

function Recado({
  titulo,
  descricao,
  acao,
}: {
  readonly titulo: string;
  readonly descricao: string;
  readonly acao: React.ReactNode;
}) {
  /* As margens da R01 (p.28): 48 px de mesa, 24 ate 1279 e 16 abaixo de 768,
     com o cartao em 24 px por dentro. Com 48 + 32 fixos, a 390 sobravam
     ~230 px para a co-marca de 276, e a assinatura da Atenas quebrava palavra
     por palavra. O titulo e `txt-secao` (21/28 Chakra, p.12): `txt-titulo`
     nao existe, e o h1 saia na letra do paragrafo. */
  return (
    <div className="flex h-full items-center justify-center overflow-auto bg-tela p-12 max-[1280px]:p-6 max-[768px]:p-4">
      <div className="coluna-formulario flex flex-col gap-6 rounded-card border border-borda-sutil bg-card p-8 max-[768px]:p-6">
        <CoMarcaLogin />
        <div className="flex flex-col gap-2">
          <h1 className="txt-secao text-pretty text-tinta">{titulo}</h1>
          <p className="txt-corpo text-pretty text-tinta-secundaria">{descricao}</p>
        </div>
        <div className="flex justify-end">{acao}</div>
      </div>
    </div>
  );
}
